import React from 'react';
import { Navigate } from 'react-router-dom';

/**
 * PermissionGuard - Componente para controlar el acceso a rutas/componentes basado en permisos
 * @param {Object} props - Props del componente
 * @param {React.ReactNode} props.children - Componentes hijos que se renderizarán si el usuario tiene permisos
 * @param {string} [props.requiredPermission] - Nombre del permiso requerido (ej: "buyorder", "notification")
 * @param {string[]} [props.requiredAnyOf] - Lista de permisos; con tener `requiredAction` en uno basta (p. ej. ['buyorder','salesorder'] para la vista combinada de Órdenes)
 * @param {string} [props.requiredAction] - Acción requerida (ej: "listView", "create", "update", "delete")
 * @param {React.ReactNode} [props.fallback] - Componente a mostrar si no tiene permisos (opcional)
 * @returns {React.ReactNode}
 */
const PermissionGuard = ({
  children,
  requiredPermission,
  requiredAnyOf,
  requiredAction = "listView",
  fallback = null
}) => {
  // Obtener información del usuario del localStorage
  const getUserData = () => {
    try {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error al obtener datos del usuario:', error);
      return null;
    }
  };

  const userData = getUserData();

  // Lista efectiva de permisos a evaluar: si llega `requiredAnyOf` se usa esa,
  // si no, se cae al modo histórico de un solo `requiredPermission`.
  const permissionNames =
    Array.isArray(requiredAnyOf) && requiredAnyOf.length > 0
      ? requiredAnyOf
      : requiredPermission
        ? [requiredPermission]
        : [];

  const hasPermission = () => {
    if (permissionNames.length === 0) {
      return false;
    }
    if (!userData || !userData.role || !userData.role.permissions) {
      return false;
    }

    return permissionNames.some((name) => {
      const permission = userData.role.permissions.find(
        (perm) => perm.permissionName === name
      );
      return permission ? permission[requiredAction] === true : false;
    });
  };

  // Si el usuario tiene el permiso requerido, mostrar los hijos
  if (hasPermission()) {
    return <>{children}</>;
  }

  // Si se proporciona un fallback, mostrarlo en lugar de redireccionar
  if (fallback) {
    return <>{fallback}</>;
  }

  // Si no tiene permiso y no hay fallback, redirigir a la página de inicio
  return <Navigate to="/home" replace />;
};

export default PermissionGuard; 